/**
 * HR Overview Tab — consolidated view of ALL HR modules aggregated into
 * a single analytics surface (headcount, attendance, performance, training,
 * leave, disciplinary, KPI, rewards, contracts, exit/turnover, skills).
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Users, UserCheck, UserX, UserPlus, Clock, Award, GraduationCap,
  FileWarning, Target, Gift, FileText, LogOut, Sparkles, Calendar,
  RefreshCw, AlertTriangle, CheckCircle2, AlertOctagon, Briefcase, Activity, DollarSign,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  SIHAT: { text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', stroke: '#10b981', icon: CheckCircle2 },
  'PERLU PANTAU': { text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', stroke: '#f59e0b', icon: AlertTriangle },
  KRITIKAL: { text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', stroke: '#ef4444', icon: AlertOctagon },
};

const ALERT_STYLE = {
  critical: 'bg-red-50 border-red-200 text-red-700',
  warning: 'bg-amber-50 border-amber-200 text-amber-700',
  info: 'bg-blue-50 border-blue-200 text-blue-700',
};

const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

const Stat = ({ icon: Icon, label, value, sub, tone = 'gray', testid }) => {
  const tones = {
    gray: 'text-gray-700 bg-gray-50',
    emerald: 'text-emerald-700 bg-emerald-50',
    amber: 'text-amber-700 bg-amber-50',
    red: 'text-red-700 bg-red-50',
    blue: 'text-blue-700 bg-blue-50',
    purple: 'text-purple-700 bg-purple-50',
  };
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500">{label}</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <div className={`p-2 rounded-lg ${tones[tone] || tones.gray}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
};

const ModuleCard = ({ title, icon: Icon, children, testid }) => (
  <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm" data-testid={testid}>
    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
      <Icon className="w-4 h-4 text-gray-500" />
      {title}
    </h3>
    {children}
  </div>
);

const HROverviewTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/staff-analytics/overview`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Ralat memuatkan data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="hr-overview-loading">
        <RefreshCw className="w-5 h-5 animate-spin text-gray-500" />
        <span className="ml-2 text-gray-500">Memuat data HR…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700" data-testid="hr-overview-error">
        {error}
        <button onClick={fetchData} className="ml-3 underline">Cuba semula</button>
      </div>
    );
  }

  if (!data) return null;

  const m = data.modules;
  const statusCfg = STATUS_STYLE[data.hr_status] || STATUS_STYLE['PERLU PANTAU'];
  const StatusIcon = statusCfg.icon;

  // Gauge
  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  const score = Math.max(0, Math.min(100, data.hr_score || 0));
  const dashoffset = circumference - (score / 100) * circumference;

  return (
    <div className="space-y-6" data-testid="hr-overview-tab">
      {/* Hero: HR Health Score + Alerts */}
      <div className={`bg-white rounded-2xl p-5 sm:p-6 border ${statusCfg.border} shadow-sm`}>
        <div className="flex flex-col lg:flex-row gap-5 lg:items-center lg:justify-between">
          <div className="flex items-center gap-4">
            <div className="relative flex-shrink-0">
              <svg width="110" height="110" viewBox="0 0 110 110" className="-rotate-90">
                <circle cx="55" cy="55" r={radius} fill="none" stroke="#f1f5f9" strokeWidth="9" />
                <circle
                  cx="55" cy="55" r={radius} fill="none"
                  stroke={statusCfg.stroke} strokeWidth="9" strokeLinecap="round"
                  strokeDasharray={circumference} strokeDashoffset={dashoffset}
                  style={{ transition: 'stroke-dashoffset 800ms ease-out' }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-bold text-gray-900" data-testid="hr-score">{score.toFixed(0)}</span>
                <span className="text-[9px] uppercase tracking-wider text-gray-400">Skor HR</span>
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold">Kesihatan HR Keseluruhan</p>
              <div className="flex items-center gap-2 mt-1">
                <StatusIcon className={`w-5 h-5 ${statusCfg.text}`} />
                <h2 className={`text-xl sm:text-2xl font-bold ${statusCfg.text}`} data-testid="hr-status">{data.hr_status}</h2>
              </div>
              <p className="text-xs text-gray-500 mt-1">{m.headcount.active} staff aktif • {m.headcount.by_department.length} jabatan</p>
            </div>
          </div>

          <button
            onClick={fetchData}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700 shadow-sm self-start"
            data-testid="hr-overview-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Muat Semula
          </button>
        </div>

        {data.alerts?.length > 0 && (
          <div className="mt-5 pt-5 border-t border-gray-100 space-y-2" data-testid="hr-alerts">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Amaran</p>
            {data.alerts.map((a, i) => (
              <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${ALERT_STYLE[a.level] || ALERT_STYLE.info}`}>
                <AlertTriangle className="w-4 h-4 shrink-0" />
                {a.msg}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat icon={Users} label="Jumlah Staff" value={m.headcount.total} sub={`${m.headcount.active} aktif`} tone="blue" testid="stat-headcount" />
        <Stat icon={UserPlus} label="Pengambilan (30h)" value={m.headcount.new_hires_30d} tone="emerald" testid="stat-new-hires" />
        <Stat icon={DollarSign} label="Kos Gaji/Bulan" value={`RM ${(m.headcount.total_monthly_payroll ?? 0).toLocaleString()}`} tone="purple" testid="stat-payroll" />
        <Stat icon={UserCheck} label="Kadar Kehadiran" value={`${m.attendance.attendance_rate}%`} sub={m.attendance.month} tone="emerald" testid="stat-attendance-rate" />
        <Stat icon={Clock} label="Rekod Lewat" value={m.attendance.late} sub="bulan ini" tone="amber" testid="stat-late" />
        <Stat icon={LogOut} label="Turnover 12B" value={`${m.exit.turnover_rate_12m}%`} sub={`${m.exit.exits_12m} keluar`} tone={m.exit.turnover_rate_12m > 20 ? 'red' : 'gray'} testid="stat-turnover" />
      </div>

      {/* Module grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Headcount by Department */}
        <ModuleCard title="Staff Mengikut Jabatan" icon={Briefcase} testid="module-headcount">
          <div style={{ width: '100%', height: 224 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={m.headcount.by_department} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={110} />
                <Tooltip />
                <Bar dataKey="count" fill="#3B82F6" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ModuleCard>

        {/* Attendance breakdown */}
        <ModuleCard title="Kehadiran Bulan Ini" icon={UserCheck} testid="module-attendance">
          {m.attendance.total_records > 0 ? (
            <div className="grid grid-cols-4 gap-2">
              <div className="bg-emerald-50 rounded-lg p-3 text-center border border-emerald-100">
                <p className="text-2xl font-bold text-emerald-700">{m.attendance.present}</p>
                <p className="text-xs text-emerald-700 font-medium mt-1">Hadir</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3 text-center border border-amber-100">
                <p className="text-2xl font-bold text-amber-700">{m.attendance.late}</p>
                <p className="text-xs text-amber-700 font-medium mt-1">Lewat</p>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center border border-red-100">
                <p className="text-2xl font-bold text-red-700">{m.attendance.absent}</p>
                <p className="text-xs text-red-700 font-medium mt-1">Tidak Hadir</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-100">
                <p className="text-2xl font-bold text-blue-700">{m.attendance.leave}</p>
                <p className="text-xs text-blue-700 font-medium mt-1">Cuti</p>
              </div>
              <div className="col-span-4 mt-2 text-xs text-gray-500">
                Punctuality: <span className="font-semibold text-gray-700">{m.attendance.punctuality_rate}%</span>
                <span className="mx-1.5 text-gray-300">•</span>
                Jumlah rekod: {m.attendance.total_records}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400 py-6 text-center">Tiada data kehadiran bulan ini</p>
          )}
        </ModuleCard>

        {/* Performance distribution */}
        <ModuleCard title="Prestasi Staff" icon={Award} testid="module-performance">
          {m.performance.total_reviews > 0 ? (
            <div className="flex items-center gap-4">
              <div style={{ width: 160, height: 160 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={m.performance.distribution} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={60} innerRadius={35}>
                      {m.performance.distribution.map((_, idx) => <Cell key={idx} fill={COLORS[idx % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-1.5 text-sm">
                <p className="text-xs text-gray-500">Skor Purata</p>
                <p className="text-3xl font-bold text-gray-900">{m.performance.avg_score}</p>
                <p className="text-xs text-gray-500">{m.performance.total_reviews} review</p>
                <div className="mt-3 space-y-1">
                  {m.performance.distribution.map((d, i) => (
                    <div key={d.name} className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                        {d.name}
                      </span>
                      <span className="font-semibold">{d.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400 py-6 text-center">Tiada data prestasi</p>
          )}
        </ModuleCard>

        {/* Training */}
        <ModuleCard title="Latihan & Pembangunan" icon={GraduationCap} testid="module-training">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500">Kadar Penyiapan</p>
              <p className="text-3xl font-bold text-emerald-600">{m.training.completion_rate}%</p>
              <p className="text-xs text-gray-400 mt-1">{m.training.completed}/{m.training.total} siap</p>
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-gray-500">Dalam proses</span><span className="font-semibold">{m.training.in_progress}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Tertangguh</span><span className="font-semibold">{m.training.pending}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Kos Terkumpul</span><span className="font-semibold">RM {m.training.total_cost.toLocaleString()}</span></div>
            </div>
          </div>
        </ModuleCard>

        {/* Leave */}
        <ModuleCard title="Permohonan Cuti" icon={Calendar} testid="module-leave">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-emerald-50 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-emerald-700">{m.leave.approved}</p>
              <p className="text-xs text-emerald-700">Diluluskan</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-amber-700">{m.leave.pending}</p>
              <p className="text-xs text-amber-700">Menunggu</p>
            </div>
            <div className="bg-red-50 rounded-lg p-3 text-center">
              <p className="text-xl font-bold text-red-700">{m.leave.rejected}</p>
              <p className="text-xs text-red-700">Ditolak</p>
            </div>
            <div className="col-span-3 text-xs text-gray-500 mt-1">
              Jumlah hari diluluskan: <span className="font-semibold text-gray-700">{m.leave.approved_days}</span>
            </div>
          </div>
        </ModuleCard>

        {/* KPI */}
        <ModuleCard title="Pencapaian KPI" icon={Target} testid="module-kpi">
          {m.kpi.total > 0 ? (
            <div className="space-y-3">
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-blue-600">{m.kpi.avg_achievement}%</span>
                <span className="text-xs text-gray-500">purata pencapaian</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${Math.min(100, m.kpi.avg_achievement)}%` }} />
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-emerald-600">On-target: {m.kpi.on_target}</span>
                <span className="text-red-600">Below-target: {m.kpi.below_target}</span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400 py-4 text-center">Tiada KPI didaftar</p>
          )}
        </ModuleCard>

        {/* Disciplinary */}
        <ModuleCard title="Tindakan Disiplin" icon={FileWarning} testid="module-disciplinary">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500">Kes Terbuka</p>
              <p className={`text-3xl font-bold ${m.disciplinary.open > 0 ? 'text-red-600' : 'text-gray-400'}`}>{m.disciplinary.open}</p>
              <p className="text-xs text-gray-400 mt-1">{m.disciplinary.resolved} selesai</p>
            </div>
            <div className="space-y-1 text-xs">
              {m.disciplinary.by_severity.length > 0 ? m.disciplinary.by_severity.map(d => (
                <div key={d.name} className="flex justify-between"><span className="text-gray-500">{d.name}</span><span className="font-semibold">{d.count}</span></div>
              )) : <p className="text-gray-400">Tiada kes</p>}
            </div>
          </div>
        </ModuleCard>

        {/* Contracts expiring */}
        <ModuleCard title="Kontrak Staff" icon={FileText} testid="module-contracts">
          <div className="space-y-2">
            <div className="flex justify-between items-baseline">
              <span className="text-xs text-gray-500">Tamat dalam 60 hari</span>
              <span className={`text-2xl font-bold ${m.contracts.expiring_soon > 0 ? 'text-amber-600' : 'text-gray-400'}`}>{m.contracts.expiring_soon}</span>
            </div>
            {m.contracts.expiring_list?.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs">
                {m.contracts.expiring_list.map((c, i) => (
                  <li key={i} className="flex justify-between py-1 border-b border-gray-50 last:border-0">
                    <span className="text-gray-700">{c.staff_name || c.staff_id}</span>
                    <span className="text-gray-500">{c.end_date}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="text-xs text-gray-400 pt-1">Jumlah kontrak: {m.contracts.total} • Tamat tempoh: {m.contracts.expired}</div>
          </div>
        </ModuleCard>

        {/* Rewards */}
        <ModuleCard title="Ganjaran & Insentif" icon={Gift} testid="module-rewards">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Jumlah Ganjaran</p>
              <p className="text-3xl font-bold text-purple-600">{m.rewards.total}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Nilai Terkumpul</p>
              <p className="text-xl font-bold text-gray-900">RM {m.rewards.total_value.toLocaleString()}</p>
            </div>
          </div>
        </ModuleCard>

        {/* Skills distribution */}
        <ModuleCard title="Kompetensi Staff" icon={Sparkles} testid="module-skills">
          {m.skills.total > 0 && m.skills.by_level.some(l => l.name.toLowerCase() !== 'unknown') ? (
            <div style={{ width: '100%', height: 192 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={m.skills.by_level}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#8B5CF6" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-gray-400 py-6 text-center">
              {m.skills.total > 0
                ? `${m.skills.total} rekod — tiada tahap kemahiran direkodkan`
                : 'Tiada data kemahiran'}
            </p>
          )}
        </ModuleCard>

        {/* Misc counts */}
        <ModuleCard title="Aktiviti HR Lain" icon={Activity} testid="module-misc">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex justify-between p-2 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Sesi Konsultasi</span>
              <span className="font-bold">{m.consultations.total}</span>
            </div>
            <div className="flex justify-between p-2 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Mesyuarat</span>
              <span className="font-bold">{m.meetings.total}</span>
            </div>
            <div className="flex justify-between p-2 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Exit Interviews</span>
              <span className="font-bold">{m.exit.total_exits}</span>
            </div>
            <div className="flex justify-between p-2 bg-gray-50 rounded-lg">
              <span className="text-gray-600">Staff Tidak Aktif</span>
              <span className="font-bold">{m.headcount.inactive}</span>
            </div>
          </div>
        </ModuleCard>
      </div>
    </div>
  );
};

export default HROverviewTab;
